import { userService } from './userService';

export interface ResearchTableConfig {
  // Table-level context that gets injected into all column AI requests
  table_context?: string; // Description of what this table is tracking (e.g., "Five Nights at Freddy's interest request campaign")
  table_goal?: string; // What we're trying to achieve with this table (e.g., "Track creator responses and manage outreach")
  
  // Companion settings
  companion_enabled?: boolean; // Whether the companion (Carl) is enabled for this table
  companion_table_goal?: string; // The specific goal/prompt for the companion on this table
  
  // Column visibility
  hidden_columns?: string[]; // Array of column IDs that are hidden
  
  columns: Array<{
    id: string;
    name: string;
    cell_type: 'normal' | 'outreach' | 'links' | 'promo' | 'payments' | 'communication' | 'relationship';
    prompt?: string;
    response_format?: 'short';
    data_type?: 'text' | 'boolean' | 'number' | 'currency' | 'percentage' | 'labels';
    google_drive_documents?: any[];
    platform_resources?: any[];
    width?: number;
    // Outreach specific
    outreach_content?: string;
    email_subject?: string;
    // Links specific
    link_type?: 'bitly' | 'custom';
    link_config?: Record<string, any>;
    // Promo specific
    promo_type?: 'shopify' | 'custom';
    promo_config?: Record<string, any>;
    // Payments specific
    payment_type?: 'stripe' | 'custom';
    payment_config?: Record<string, any>;
    // Labels specific
    label_options?: string[];
    // Communication specific
    requires_thread?: boolean;
    communication_sources?: ('email_history' | 'outlook_conversations' | 'gmail_conversations' | 'instagram_messages')[];
    // Relationship specific
    relationship_config?: {
      prompt: string;
      result_format?: 'number' | 'currency' | 'percentage' | 'text';
    };
  }>;
  context?: Array<{
    id: string;
    name: string;
    document_ref?: string;
    required: boolean;
    copyable: boolean;
    placeholder?: string;
    created_at: string;
  }>;
  context_required?: boolean; // Whether this table requires context to be complete
}

export interface ResearchTable {
  id: string;
  name: string;
  description: string;
  table_config: ResearchTableConfig;
  created_at: string;
  updated_at: string;
  column_count: number;
  project_id?: string;
  project_info?: {
    project_uuid: string;
    project_name: string;
    project_color: string;
  };
  template?: string;
  table_type?: string;
  is_system_table?: boolean; // System tables cannot be deleted
  system_table_type?: string; // e.g., 'status', 'metrics'
}

export interface CreateResearchTableRequest {
  project_id?: string; // Optional since tables don't have to be part of a project
  name: string;
  description?: string;
  table_config?: Partial<ResearchTableConfig>;
  template?: string;
  table_type?: string;
}

export interface UpdateResearchTableRequest {
  name?: string;
  description?: string;
  table_config?: ResearchTableConfig;
  project_id?: string;
  table_type?: string;
}

// Creator data interfaces
export interface CreatorCellData {
  value: string;
  loading?: boolean;
  error?: string | null;
  reasoning?: string;
  source?: string;
  confidence?: string;
  sources_used?: string[];
  fetch_time?: number;
  timestamp?: string;
  research_metadata?: {
    prompt?: string;
    response_format?: string;
    data_type?: string;
    platform_resources?: any[];
  };
  updated_at?: string;
  // Conversation-specific fields for email thread display
  messages?: Array<{
    message_id: string;
    user_type: 'USER' | 'CREATOR';
    sender: string;
    subject?: string;
    received_at: string;
    content: string;
    is_target?: boolean;
    has_attachments?: boolean;
  }>;
  highlight_text?: string;
  source_message_id?: string;
  context_found_in_message_id?: string;
  // Pending change system - for updates to cells that already have values
  pending_change?: {
    value: string;
    reasoning?: string;
    source?: string;
    confidence?: string;
    proposed_at: string;
    proposed_by: 'user' | 'ai';
    messages?: Array<{
      message_id: string;
      user_type: 'USER' | 'CREATOR';
      sender: string;
      subject?: string;
      received_at: string;
      content: string;
      is_target?: boolean;
      has_attachments?: boolean;
    }>;
    highlight_text?: string;
    source_message_id?: string;
  };
}

export interface CellUpdate {
  creatorId: string;
  columnId: string;
  cellData: CreatorCellData;
}

export interface TableCreatorsResponse {
  creators: any[];
  count: number;
}

export interface TableDataResponse {
  tableData: Record<string, Record<string, CreatorCellData>>;
}

class ResearchTableService {
  private baseUrl = (() => {
    const fromEnv = process.env.REACT_APP_API_URL;
    if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
    if (typeof window !== 'undefined') {
      const host = window.location.host || '';
      const hostname = window.location.hostname || '';
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
      const isDevSite = host.includes('saas-application-dev.s3-website.eu-north-1.amazonaws.com');
      if (isLocalhost || isDevSite) return 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';
    }
    return 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';
  })();

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const token = await userService.getAuthToken();
    
    console.log(`🔍 [ResearchTableService] Making request to: ${this.baseUrl}${endpoint}`);
    console.log(`🔍 [ResearchTableService] Method: ${options.method || 'GET'}`);
    console.log(`🔍 [ResearchTableService] Headers:`, {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token ? token.substring(0, 20) + '...' : 'NO_TOKEN'}`,
      ...options.headers,
    });
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    console.log(`📊 [ResearchTableService] Response status: ${response.status}`);
    console.log(`📊 [ResearchTableService] Response headers:`, Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`❌ [ResearchTableService] Error response:`, errorData);
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    // Handle 204 No Content responses (like DELETE)
    if (response.status === 204) {
      console.log(`✅ [ResearchTableService] Success response: No content (204)`);
      return null;
    }

    const data = await response.json();
    console.log(`✅ [ResearchTableService] Success response:`, data);
    return data;
  }

  /**
   * List all research tables for a project (or all tables if no projectId)
   */
  async getResearchTables(projectId?: string): Promise<ResearchTable[]> {
    const queryParam = projectId ? `?projectId=${projectId}` : '';
    const data = await this.makeRequest(`/research-tables${queryParam}`);
    return data.tables || [];
  }

  /**
   * Get a specific research table
   */
  async getResearchTable(tableId: string): Promise<ResearchTable> {
    const data = await this.makeRequest(`/research-tables/${tableId}`);
    return data.table;
  }

  /**
   * Create a new research table
   */
  async createResearchTable(request: CreateResearchTableRequest): Promise<ResearchTable> {
    // Provide default config structure - only columns
    const defaultConfig: ResearchTableConfig = {
      columns: []
    };

    const payload = {
      ...request,
      table_config: {
        ...defaultConfig,
        ...request.table_config
      }
    };

    const data = await this.makeRequest('/research-tables', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    
    return data.table;
  }

  /**
   * Update a research table
   */
  async updateResearchTable(tableId: string, request: UpdateResearchTableRequest): Promise<ResearchTable> {
    const data = await this.makeRequest(`/research-tables/${tableId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
    
    return data.table;
  }

  /**
   * Delete a research table
   */
  async deleteResearchTable(tableId: string): Promise<void> {
    await this.makeRequest(`/research-tables/${tableId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Save table configuration (columns, settings, etc.)
   */
  async saveTableConfig(tableId: string, config: ResearchTableConfig): Promise<ResearchTable> {
    return this.updateResearchTable(tableId, { table_config: config });
  }

  /**
   * Assign a project to a research table
   */
  async assignProjectToTable(tableId: string, projectId: string | null): Promise<ResearchTable> {
    const data = await this.makeRequest(`/research-tables/${tableId}/assign-project`, {
      method: 'PUT',
      body: JSON.stringify({ project_id: projectId }),
    });
    
    return data;
  }

  // ================================
  // Creator Data Management Methods
  // ================================

  /**
   * Get all creators associated with a research table
   */
  async getTableCreators(tableId: string): Promise<TableCreatorsResponse> {
    const data = await this.makeRequest(`/research-tables/${tableId}/creators`);
    return data;
  }

  /**
   * Add creators to a research table
   */
  async addCreatorsToTable(tableId: string, creatorIds: string[]): Promise<{ message: string; creatorsAdded: number; cellsCreated: number }> {
    const data = await this.makeRequest(`/research-tables/${tableId}/creators`, {
      method: 'POST',
      body: JSON.stringify({ creatorIds }),
    });
    return data;
  }

  /**
   * Remove a creator from a research table
   */
  async removeCreatorFromTable(tableId: string, creatorId: string): Promise<{ message: string; cellsDeleted: number }> {
    const data = await this.makeRequest(`/research-tables/${tableId}/creators/${creatorId}`, {
      method: 'DELETE',
    });
    return data;
  }

  // ================================
  // Cell Data Management Methods
  // ================================

  /**
   * Get all cell data for a research table
   */
  async getTableData(tableId: string): Promise<TableDataResponse> {
    const data = await this.makeRequest(`/research-tables/${tableId}/data`);
    return data;
  }

  /**
   * Get a single cell's data from a research table
   */
  async getCellData(projectId: string, tableId: string, creatorId: string, columnId: string): Promise<CreatorCellData | null> {
    try {
      const data = await this.getTableData(tableId);
      const cellData = data.tableData?.[creatorId]?.[columnId];
      return cellData || null;
    } catch (error) {
      console.error(`❌ [ResearchTableService] Failed to get cell data:`, error);
      return null;
    }
  }

  /**
   * Batch update multiple cells in a research table
   */
  async updateTableCellData(tableId: string, cellUpdates: CellUpdate[]): Promise<{ message: string; cellsUpdated: number }> {
    const data = await this.makeRequest(`/research-tables/${tableId}/data`, {
      method: 'PUT',
      body: JSON.stringify({ cellUpdates }),
    });
    return data;
  }

  /**
   * Update a single cell in a research table
   */
  async updateSingleCell(tableId: string, creatorId: string, columnId: string, cellData: CreatorCellData): Promise<{ cellData: CreatorCellData }> {
    const data = await this.makeRequest(`/research-tables/${tableId}/cells/${creatorId}/${columnId}`, {
      method: 'PUT',
      body: JSON.stringify({ cellData }),
    });
    return data;
  }

  /**
   * Accept a pending change by promoting it to the main cell value
   */
  async acceptPendingChange(tableId: string, creatorId: string, columnId: string): Promise<{ cellData: CreatorCellData }> {
    // First, get the current cell data to access the pending change
    const tableDataResponse = await this.getTableData(tableId);
    const cellData = tableDataResponse.tableData[creatorId]?.[columnId];
    
    if (!cellData?.pending_change) {
      throw new Error('No pending change to accept');
    }

    // Promote the pending change to the main value
    const updatedCellData: CreatorCellData = {
      value: cellData.pending_change.value,
      reasoning: cellData.pending_change.reasoning,
      source: cellData.pending_change.source,
      confidence: cellData.pending_change.confidence,
      messages: cellData.pending_change.messages,
      highlight_text: cellData.pending_change.highlight_text,
      source_message_id: cellData.pending_change.source_message_id,
      loading: false,
      error: undefined,
      pending_change: undefined, // Clear the pending change
      timestamp: new Date().toISOString(),
    };

    // Save the updated cell data
    return this.updateSingleCell(tableId, creatorId, columnId, updatedCellData);
  }

  /**
   * Dismiss a pending change without applying it
   */
  async dismissPendingChange(tableId: string, creatorId: string, columnId: string): Promise<{ cellData: CreatorCellData }> {
    // First, get the current cell data
    const tableDataResponse = await this.getTableData(tableId);
    const cellData = tableDataResponse.tableData[creatorId]?.[columnId];
    
    if (!cellData) {
      throw new Error('Cell data not found');
    }

    // Just remove the pending change
    const updatedCellData: CreatorCellData = {
      ...cellData,
      pending_change: undefined,
    };

    // Save the updated cell data
    return this.updateSingleCell(tableId, creatorId, columnId, updatedCellData);
  }

  /**
   * Reorder tables by updating workflow_order
   */
  async reorderTables(tableOrders: { tableId: string; workflowOrder: number }[]): Promise<{ message: string; tablesUpdated: number }> {
    const data = await this.makeRequest('/research-tables/reorder', {
      method: 'PUT',
      body: JSON.stringify({ tableOrders }),
    });
    return data;
  }

  // ================================
  // Track Posts Methods
  // ================================

  /**
   * Trigger AI-powered post search for a creator
   */
  async searchPosts(tableId: string, payload: { creator_id: string; column_id: string }): Promise<{ success: boolean; message: string; status: string }> {
    const data = await this.makeRequest(`/research-tables/${tableId}/search-posts`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data;
  }

  /**
   * Verify or reject a discovered post
   */
  async verifyPost(tableId: string, payload: { creator_id: string; column_id: string; verification: 'verified' | 'rejected' | 'pending_verification' }): Promise<{ success: boolean; message: string; cellData: any }> {
    const data = await this.makeRequest(`/research-tables/${tableId}/verify-post`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data;
  }

  /**
   * Update companion settings for a table
   */
  async updateCompanionSettings(tableId: string, companionEnabled: boolean, companionTableGoal: string): Promise<ResearchTable> {
    // Get current table config
    const table = await this.getResearchTable(tableId);
    
    // Update companion settings in config
    const updatedConfig = {
      ...table.table_config,
      companion_enabled: companionEnabled,
      companion_table_goal: companionTableGoal
    };
    
    // Save updated config
    return await this.updateResearchTable(tableId, {
      table_config: updatedConfig
    });
  }

}

/**
 * Calculate relationship value using AI based on prompt and all available row data
 */
export async function calculateRelationshipValueWithAI(
  allRowData: Record<string, string>, // columnId -> value mapping for entire row
  columnNames: Record<string, string>, // columnId -> column name mapping
  prompt: string,
  resultFormat?: 'number' | 'currency' | 'percentage' | 'text'
): Promise<string> {
  try {
    // Build context for the AI with all available column data
    const contextData = Object.entries(allRowData)
      .filter(([columnId, value]) => value && value.trim() !== '') // Only include columns with data
      .map(([columnId, value]) => {
        const columnName = columnNames[columnId] || columnId;
        return `${columnName}: ${value}`;
      })
      .join('\n');

    const fullPrompt = `Based on the following data for this creator:
${contextData}

${prompt}

Please provide only the calculated result without any explanation or additional text.`;

    // TODO: Replace with actual AI service call
    // For now, return a placeholder that shows the concept works
    console.log('🤖 AI Calculation Request:', { allRowData, columnNames, prompt, resultFormat });
    
    // This would be replaced with actual AI service call:
    // const response = await aiService.calculateValue(fullPrompt);
    // return formatAIResult(response, resultFormat);
    
    // Placeholder logic for demonstration
    const numericValues = Object.values(allRowData)
      .map(v => parseFloat(v.toString().replace(/[^0-9.-]/g, '')))
      .filter(v => !isNaN(v));
    
    if (numericValues.length >= 2) {
      const result = numericValues[0] / numericValues[1]; // Simple division as example
      return formatCalculationResult(result, resultFormat);
    }
    
    return 'Calculating...';
  } catch (error) {
    console.error('AI calculation error:', error);
    return 'Error';
  }
}

/**
 * Format calculation result based on specified format
 */
function formatCalculationResult(value: number, format?: 'number' | 'currency' | 'percentage' | 'text'): string {
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    case 'percentage':
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(value / 100);
    case 'number':
    default:
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value);
  }
}

export const researchTableService = new ResearchTableService();
export default researchTableService;